import { FunctionComponent, useEffect, useState } from 'react'
import styles from './DaycareProfileHandler.module.scss'
import DaycareProfile from './profile/daycareProfile'
import DaycareSettings from './settings/daycareSettings'
import DaycareStaffSettings from './staff/daycareStaffSettings'
import NewsSettings from './newsSettings/newsSettings'
import DaycareMerger from './merger/daycareMerger'
import DaycareExport from './export/export'
import Customisation from './customisation/customisation'
import { Role } from '@/types/Role'
import { Divider, Drawer, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import Assignment from '@mui/icons-material/Assignment'
import AutoFixHigh from '@mui/icons-material/AutoFixHigh'
import BrowserUpdated from '@mui/icons-material/BrowserUpdated'
import EditCalendar from '@mui/icons-material/EditCalendar'
import Groups from '@mui/icons-material/Groups'
import House from '@mui/icons-material/House'
import Info from '@mui/icons-material/Info'
import ListIcon from '@mui/icons-material/List'
import Share from '@mui/icons-material/Share'
import Link from '@/components/_custom/Link/Link'
import { useTranslation } from 'react-i18next'
import DaycareParentProfile from './profile/daycareParentProfile'
import { Navigate, NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import { Daycare } from '@/types/Daycare'
import SuggestSettings from './suggestSettings/suggestSettings'
import AutolinkSettings from './autolinkSettings/autolinkSettings'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector'
import { Restaurant, Sms } from '@mui/icons-material'
import DaycareMeal from '@/container/daycare/daycareProfile/meal/meal.tsx'

interface DaycareProfileHandlerProps {
    daycareId: number
}

enum Modal {
    profile = 'profile',
    settings = 'settings',
    staffSettings = 'staffSettings',
    merger = 'merger',
    suggest = 'suggest',
    custom = 'custom',
    export = 'export',
    autolink = 'autolink',
    meal = 'meal',
    newsSettings = 'newsSettings'
}

const DaycareProfileHandler: FunctionComponent<DaycareProfileHandlerProps> = ({
    daycareId
}: DaycareProfileHandlerProps) => {
    const { t } = useTranslation()

    const { user, setCurrentDaycare, fetchCurrentDaycare } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [openMobileMenu, setOpenMobileMenu] = useState<boolean>(false)
    const [activeModal, setActiveModal] = useState<Modal>(Modal.profile)

    function infosDaycare(daycareId: number): void {
        setCurrentDaycare(daycareId)

        if (!user?.currentDaycare) {
            navigate('/daycare')
        }

        if (user?.role != Role.parent) {
            const searchParams: string = window.location.hash.substring(1)
            switch (searchParams) {
                case 'settings':
                    setActiveModal(Modal.settings)
                    break
                case 'staffSettings':
                    setActiveModal(Modal.staffSettings)
                    break
                case 'merger':
                    setActiveModal(Modal.merger)
                    break
                case 'suggest':
                    setActiveModal(Modal.suggest)
                    break
                case 'custom':
                    setActiveModal(Modal.custom)
                    break
                case 'autolink':
                    setActiveModal(Modal.autolink)
                    break
                case 'export':
                    setActiveModal(Modal.export)
                    break
                case 'meal':
                    setActiveModal(Modal.meal)
                    break
                case 'newsSettings':
                    setActiveModal(Modal.newsSettings)
                    break
                default:
                    setActiveModal(Modal.profile)
            }
        }
    }

    useEffect((): void => {
        infosDaycare(Number(daycareId))
    }, [daycareId])

    function refetchDaycare(): void {
        fetchCurrentDaycare().then(() => infosDaycare(Number(daycareId)))
    }

    function showProfile(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#profile')
        setOpenMobileMenu(false)
        setActiveModal(Modal.profile)
    }

    function showSettings(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#settings')
        setOpenMobileMenu(false)
        setActiveModal(Modal.settings)
    }

    function showStaffSettings(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#staffSettings')
        setOpenMobileMenu(false)
        setActiveModal(Modal.staffSettings)
    }

    function showMerger(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#merger')
        setOpenMobileMenu(false)
        setActiveModal(Modal.merger)
    }

    function showSuggest(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#suggest')
        setOpenMobileMenu(false)
        setActiveModal(Modal.suggest)
    }

    function showCustom(): void {
        navigate(location.pathname + location.search + '#custom')
        setOpenMobileMenu(false)
        setActiveModal(Modal.custom)
    }

    function showAutolink(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#autolink')
        setOpenMobileMenu(false)
        setActiveModal(Modal.autolink)
    }

    function showExport(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#export')
        setOpenMobileMenu(false)
        setActiveModal(Modal.export)
    }

    function showMeal(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#meal')
        setOpenMobileMenu(false)
        setActiveModal(Modal.meal)
    }

    function showNewsSettings(): void {
        refetchDaycare()
        navigate(location.pathname + location.search + '#newsSettings')
        setOpenMobileMenu(false)
        setActiveModal(Modal.newsSettings)
    }

    useEffect(() => {
        document.querySelector('.view')?.scrollTo({ top: 0 })
    }, [activeModal])

    const [daycare, setDaycare] = useState<Daycare | undefined>(user?.currentDaycare)

    useEffect(() => {
        setDaycare(undefined)
        if (user?.currentDaycare) {
            setDaycare(user.currentDaycare)
        }
    }, [user?.currentDaycare, user?.currentDaycare?.opening])

    return user && daycare ? (
        <>
            {user.role === Role.parent ? (
                <DaycareParentProfile daycare={daycare} />
            ) : (
                <div className={styles.ProfileHandlerContainer}>
                    <div
                        className={`${styles.ProfileHandlerMenu} 
										 ${openMobileMenu ? styles.ProfileHandlerMenuOpen : ''}`}
                    >
                        <Drawer variant='permanent' className={styles.ProfileHandlerMenuDrawer}>
                            {user.daycares.length > 1 && (
                                <>
                                    {user.role === Role.staff ? (
                                        <div className={styles.SelectDaycare}>
                                            <DaycareSelector removeBackground />
                                        </div>
                                    ) : (
                                        <>
                                            <Link href={'/daycare'}>
                                                <ListItemButton>
                                                    <ListItemIcon>
                                                        <ArrowBackIos />
                                                    </ListItemIcon>
                                                    <ListItemText primary={t('backToList')} />
                                                </ListItemButton>
                                            </Link>
                                            <Divider />
                                        </>
                                    )}
                                </>
                            )}
                            <List>
                                <ListItemButton onClick={showProfile}>
                                    <ListItemIcon>
                                        <Info className={activeModal === Modal.profile ? styles.selectedIcon : ''} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t('daycareInformation')}
                                        className={activeModal === Modal.profile ? styles.selectedIcon : ''}
                                    />
                                </ListItemButton>
                                {user.currentDaycareRights.canReadParamsDaycare && (
                                    <>
                                        <ListItemButton onClick={showSettings}>
                                            <ListItemIcon>
                                                <House
                                                    className={
                                                        activeModal === Modal.settings ? styles.selectedIcon : ''
                                                    }
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={t('daycareSettings')}
                                                className={activeModal === Modal.settings ? styles.selectedIcon : ''}
                                            />
                                        </ListItemButton>

                                        {user.currentDaycareRights.hasStaffAccess && (
                                            <ListItemButton onClick={showStaffSettings}>
                                                <ListItemIcon>
                                                    <Groups
                                                        className={
                                                            activeModal === Modal.staffSettings
                                                                ? styles.selectedIcon
                                                                : ''
                                                        }
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={t('staffSettings')}
                                                    className={
                                                        activeModal === Modal.staffSettings ? styles.selectedIcon : ''
                                                    }
                                                />
                                            </ListItemButton>
                                        )}

                                        <ListItemButton onClick={showMerger}>
                                            <ListItemIcon>
                                                <EditCalendar
                                                    className={activeModal === Modal.merger ? styles.selectedIcon : ''}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={t('reservationSettings')}
                                                className={activeModal === Modal.merger ? styles.selectedIcon : ''}
                                            />
                                        </ListItemButton>
                                        {user.currentDaycareRights.hasSuggestAccess && (
                                            <ListItemButton onClick={showSuggest}>
                                                <ListItemIcon>
                                                    <Assignment
                                                        className={
                                                            activeModal === Modal.suggest ? styles.selectedIcon : ''
                                                        }
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={t('suggestSettings')}
                                                    className={activeModal === Modal.suggest ? styles.selectedIcon : ''}
                                                />
                                            </ListItemButton>
                                        )}
                                        {user.currentDaycareRights.hasMealAccess && (
                                            <ListItemButton onClick={showMeal}>
                                                <ListItemIcon>
                                                    <Restaurant
                                                        className={
                                                            activeModal === Modal.meal ? styles.selectedIcon : ''
                                                        }
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={t('mealSettings')}
                                                    className={activeModal === Modal.meal ? styles.selectedIcon : ''}
                                                />
                                            </ListItemButton>
                                        )}
                                        {user.currentDaycareRights.hasNewsAccess && (
                                            <ListItemButton onClick={showNewsSettings}>
                                                <ListItemIcon>
                                                    <Sms
                                                        className={
                                                            activeModal === Modal.newsSettings
                                                                ? styles.selectedIcon
                                                                : ''
                                                        }
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={t('newsSettings')}
                                                    className={
                                                        activeModal === Modal.newsSettings ? styles.selectedIcon : ''
                                                    }
                                                />
                                            </ListItemButton>
                                        )}
                                        <ListItemButton onClick={showCustom}>
                                            <ListItemIcon>
                                                <AutoFixHigh
                                                    className={activeModal === Modal.custom ? styles.selectedIcon : ''}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={t('customisation')}
                                                className={activeModal === Modal.custom ? styles.selectedIcon : ''}
                                            />
                                        </ListItemButton>
                                        <ListItemButton onClick={showAutolink}>
                                            <ListItemIcon>
                                                <Share
                                                    className={
                                                        activeModal === Modal.autolink ? styles.selectedIcon : ''
                                                    }
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={t('autoLink')}
                                                className={activeModal === Modal.autolink ? styles.selectedIcon : ''}
                                            />
                                        </ListItemButton>
                                    </>
                                )}
                                {(user.currentDaycareRights.canReadOccasionalSlot ||
                                    user.currentDaycareRights.canReadFamilies ||
                                    user.currentDaycareRights.canReadContracts ||
                                    user.currentDaycareRights.canReadEvents ||
                                    user.currentDaycareRights.canReadTaskNote ||
                                    user.currentDaycareRights.canReadParamsDaycare ||
                                    user.currentDaycareRights.canReadStaffRights ||
                                    user.currentDaycareRights.canReadStaffWorks) && (
                                    <ListItemButton onClick={showExport}>
                                        <ListItemIcon>
                                            <BrowserUpdated
                                                className={activeModal === Modal.export ? styles.selectedIcon : ''}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={t('export')}
                                            className={activeModal === Modal.export ? styles.selectedIcon : ''}
                                        />
                                    </ListItemButton>
                                )}
                            </List>
                        </Drawer>
                    </div>
                    <div className={styles.ProfilHandlerMenuHidden}></div>
                    <div
                        className={`${styles.ProfileHandlerContent} ${
                            openMobileMenu ? styles.HideProfileHandlerContent : styles.ProfileHandlerContentFull
                        }`}
                    >
                        {activeModal === Modal.profile && <DaycareProfile daycare={daycare} role={user.role!} />}
                        {user.currentDaycareRights.canReadParamsDaycare && (
                            <>
                                {activeModal === Modal.settings && <DaycareSettings daycare={daycare} />}
                                {activeModal === Modal.staffSettings && user.currentDaycareRights.hasStaffAccess && (
                                    <DaycareStaffSettings daycare={daycare} />
                                )}
                                {activeModal === Modal.merger && <DaycareMerger daycare={daycare} />}
                                {activeModal === Modal.suggest && user.currentDaycareRights.hasSuggestAccess && (
                                    <SuggestSettings daycare={daycare} />
                                )}
                                {activeModal === Modal.custom && <Customisation daycare={daycare} />}
                                {activeModal === Modal.autolink && <AutolinkSettings daycare={daycare} />}
                                {activeModal === Modal.meal && <DaycareMeal />}
                                {activeModal === Modal.newsSettings && user.currentDaycareRights.hasNewsAccess && (
                                    <NewsSettings daycareId={daycare.id} />
                                )}
                            </>
                        )}
                        {activeModal === Modal.export &&
                            (user.currentDaycareRights.canReadOccasionalSlot ||
                                user.currentDaycareRights.canReadFamilies ||
                                user.currentDaycareRights.canReadContracts ||
                                user.currentDaycareRights.canReadEvents ||
                                user.currentDaycareRights.canReadTaskNote ||
                                user.currentDaycareRights.canReadParamsDaycare ||
                                user.currentDaycareRights.canReadStaffRights ||
                                user.currentDaycareRights.canReadStaffWorks) && <DaycareExport daycare={daycare} />}
                    </div>
                    <a
                        className={`${styles.OpenMenuButton} ${openMobileMenu ? styles.OpenMenuButtonHide : ''}`}
                        onClick={() => setOpenMobileMenu(true)}
                    >
                        <ListIcon />
                    </a>
                </div>
            )}
        </>
    ) : (
        <Navigate to='/daycare' />
    )
}

export default DaycareProfileHandler
